import React, { useMemo, useContext } from 'react';
import { safeJsonParse } from '../../../../../utils/jsonRepairUtils';
import { FactCheckView } from '../FactCheckView';
import { FactCheckLookupContext } from '../FactCheckLookupContext';
import { useDebouncedValue } from './useDebouncedValue';
import { ErrorBoundary } from './ErrorBoundary';
import '../ExtendedMarkdown.css';

import { useTranslation } from 'react-i18next';

interface MarkdownFactCheckProps {
  code: string;
}

function extractFindingId(parsed: unknown): string | null {
  if (!parsed || typeof parsed !== 'object') {
    return null;
  }
  const obj = parsed as Record<string, unknown>;
  const id =
    typeof obj.findingId === 'string' && obj.findingId.trim() !== ''
      ? obj.findingId.trim()
      : null;
  return id;
}

export const MarkdownFactCheck: React.FC<MarkdownFactCheckProps> = ({
  code,
}) => {
  const { t } = useTranslation('pleri/chat');
  const debouncedCode = useDebouncedValue(code, 300, 500);
  const { getVerificationForFindingId } = useContext(FactCheckLookupContext);

  const parsed = useMemo(() => {
    const trimmed = debouncedCode.trim();
    if (!trimmed) {
      return { kind: 'empty' as const };
    }
    let value: unknown;
    try {
      value = safeJsonParse(trimmed, 'fact check markdown');
    } catch {
      return { kind: 'error' as const, error: 'Invalid JSON' };
    }
    const findingId = extractFindingId(value);
    if (findingId) {
      return { kind: 'findingId' as const, findingId };
    }
    return {
      kind: 'error' as const,
      error: 'Expected { findingId }',
    };
  }, [debouncedCode]);

  const factCheckFromLookup =
    parsed.kind === 'findingId'
      ? getVerificationForFindingId(parsed.findingId)
      : null;

  const codeLooksComplete = code.trim().endsWith('}');
  const isStreaming =
    code.trim() !== '' && (code !== debouncedCode || !codeLooksComplete);

  if (isStreaming) {
    return (
      <div className="my-4 h-24 animate-pulse rounded-xl border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800" />
    );
  }

  if (parsed.kind === 'error') {
    return (
      <p className="my-4 text-sm text-gray-500 dark:text-gray-400">
        {t('factCheck', { error: parsed.error })}
      </p>
    );
  }

  if (parsed.kind === 'empty') {
    return null;
  }

  if (factCheckFromLookup) {
    return (
      <ErrorBoundary
        fallback={
          <p className="my-4 text-sm text-gray-500 dark:text-gray-400">
            {t('factCheckingDetailsCouldNotBeDisplayed')}
          </p>
        }
      >
        <FactCheckView
          verification={factCheckFromLookup.verification}
          findingStatus={factCheckFromLookup.findingStatus}
        />
      </ErrorBoundary>
    );
  }

  return (
    <p className="my-4 text-sm text-gray-500 dark:text-gray-400">
      {t('factCheckingDetailsNotAvailableInThis')}
    </p>
  );
};
